const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.realgram.ru';

class ApiService {
  // Нормализуем валюту к ISO 4217 коду
  normalizeCurrency(currency) {
    if (!currency) return 'RUB';
    const c = String(currency).trim().toUpperCase();
    const map = {
      RUR: 'RUB',
      RUB: 'RUB',
      USD: 'USD',
      EUR: 'EUR',
      AED: 'AED',
      TRY: 'TRY',
      THB: 'THB',
      // распространённые символы/варианты
      '₽': 'RUB',
      'РУБ': 'RUB',
      'РУБ.': 'RUB',
      'РУБЛЕЙ': 'RUB',
      'РУБЛЬ': 'RUB',
      '$': 'USD',
      '€': 'EUR',
      'د.إ': 'AED',
      '₺': 'TRY',
      '฿': 'THB'
    };
    // Попробуем прямое сопоставление
    if (map[c]) return map[c];
    // Попробуем привести кириллицу к RUB
    const cNoDots = c.replace(/\./g, '');
    if (map[cNoDots]) return map[cNoDots];
    // Если формат вида "руб." в неверной кодировке (например, ÑÑÐ±.) — fallback
    try {
      // если это уже валидный ISO-код из 3 букв
      if (/^[A-Z]{3}$/.test(c)) return c;
    } catch (e) {
      // ignore
    }
    return 'RUB';
  }
  // Получаем app_id из URL
  getAppIdFromUrl() {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return pathParts.length > 0 && uuidRegex.test(pathParts[0]) ? pathParts[0] : null;
  }

  // Создаем заголовки с app_id если он есть
  getHeaders(additionalHeaders = {}) {
    const headers = { ...additionalHeaders };
    const appId = this.getAppIdFromUrl();
    if (appId) {
      headers['X-App-Id'] = appId;
    }
    return headers;
  }

  async fetchProperties() {
    try {
      const appId = this.getAppIdFromUrl();
      const url = appId
        ? `${API_BASE_URL}/properties?app_id=${encodeURIComponent(appId)}`
        : `${API_BASE_URL}/properties`;
      const response = await fetch(url, {
        headers: this.getHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching properties:', error);
      throw error;
    }
  }

  async fetchProperty(id) {
    try {
      const appId = this.getAppIdFromUrl();
      const url = appId
        ? `${API_BASE_URL}/properties/${id}?app_id=${encodeURIComponent(appId)}`
        : `${API_BASE_URL}/properties/${id}`;
      const response = await fetch(url, {
        headers: this.getHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching property ${id}:`, error);
      throw error;
    }
  }

  async identifyUser({ externalId, name, username, appId }) {
    const form = new FormData();
    form.append('external_id', externalId);
    if (name) form.append('name', name);
    if (username) form.append('username', username);

    // Используем переданный appId или берем из URL
    const currentAppId = appId || this.getAppIdFromUrl();
    if (currentAppId) form.append('application_id', currentAppId);
    const headers = currentAppId ? { 'X-App-Id': currentAppId } : {};

    const res = await fetch(`${API_BASE_URL}/users/identify`, {
      method: 'POST',
      body: form,
      headers
    });
    if (!res.ok) throw new Error('Failed to identify user');
    return res.json();
  }

  async logPropertyView({ propertyId, externalUserId }) {
    const form = new FormData();
    if (externalUserId) form.append('external_user_id', externalUserId);
    const appId = this.getAppIdFromUrl();
    if (appId) form.append('application_id', appId);

    const res = await fetch(`${API_BASE_URL}/properties/${propertyId}/view`, {
      method: 'POST',
      body: form,
      headers: this.getHeaders()
    });
    if (!res.ok) throw new Error('Failed to log view');
    return res.json();
  }

  async logSearch({ query, city, type, minPrice, maxPrice, minRooms, externalUserId }) {
    const form = new FormData();
    if (query) form.append('query', query);
    if (city) form.append('city', city);
    if (type) form.append('type', type);
    if (minPrice != null && minPrice !== '') form.append('min_price', String(minPrice));
    if (maxPrice != null && maxPrice !== '') form.append('max_price', String(maxPrice));
    if (minRooms != null && minRooms !== '') form.append('min_rooms', String(minRooms));
    if (externalUserId) form.append('external_user_id', externalUserId);
    const appId = this.getAppIdFromUrl();
    if (appId) form.append('application_id', appId);

    const res = await fetch(`${API_BASE_URL}/search/log`, {
      method: 'POST',
      body: form,
      headers: this.getHeaders()
    });
    if (!res.ok) throw new Error('Failed to log search');
    return res.json();
  }

  formatPrice(price, currency = 'RUB') {
    const code = this.normalizeCurrency(currency);
    try {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 0,
      }).format(price ?? 0);
    } catch (e) {
      // Fallback на RUB при некорректном коде
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 0,
      }).format(price ?? 0);
    }
  }

  getPropertyTypeLabel(type) {
    const labels = {
      'apartment': 'Квартира',
      'villa': 'Вилла',
      'house': 'Дом',
      'studio': 'Студия'
    };
    return labels[type] || type;
  }
}

export default new ApiService();
